import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { AccordionModule } from 'primeng/accordion';
import { FilterMatchMode, FilterMetadata, FilterService, MessageService, SelectItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

import { firstValueFrom, forkJoin } from 'rxjs';
import { ProcessingComponent } from '../../../components';

import {
  DictionaryCacheService,
  IAppUser,
  InsectaryDashboardService,
  ISearchFilterValue,
  ISearchRequest,
  ISearchRequestFilter,
  ISearchSort,
  OrderItemStatus,
  OrderStatus
} from '../../../services/insecta';

import { afterRender } from '../../../common/after-render';
import { notifyAndTrackError } from '../../../common/error';
import { getItemDescriptionColumn, isNotEmpty, isStringEmpty, toShortDate } from '../../../common/functions';
import { AppInitService, IInsectaPermissions } from '../../../services/app-init.service';
import { AppInsightsService } from '../../../services/app-insights.service';
import { InsectaryCalendarComponent } from './insectary-calendar/insectary-calendar.component';

@Component({
  selector: 'app-insectary-dashboard',
  standalone: true,
  imports: [
    BadgeModule,
    CommonModule,
    ButtonModule,
    FileUploadModule,
    FormsModule,
    ImageModule,
    InputTextModule,
    TableModule,
    ToggleSwitchModule,
    TooltipModule,
    RouterModule,
    ProcessingComponent,
    PaginatorModule,
    InputNumberModule,
    DialogModule,
    PanelModule,
    AccordionModule,
    SelectButtonModule,
    InsectaryCalendarComponent
  ],
  templateUrl: './insectary-dashboard.component.html'
})
export class InsectaryDashboardComponent implements OnInit {
  @ViewChild('insectaryCalendar') insectaryCalendar!: InsectaryCalendarComponent;
  @ViewChild('dt') dt!: Table;

  @Input() appUser!: IAppUser;
  userSettingsJson: { [key: string]: any } = {};
  route: any;

  permissions!: IInsectaPermissions;

  pageVars: any = {
    currentUser: ``,
    isReady: false,
    undeliveravbleDates: <any[]>[],
    undeliveravbleDays: <any[]>[]
  };

  tableColumns: string[] = [
    'OrderId',
    'OrderSequence',
    'RequesterName',
    'ItemType',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'FirstDelivery',
    'LastDelivery',
    'InstanceCount',
    'ItemStatus',
    'OrderName',
    'ItemSequence'
  ];

  orders: any[] = [];

  skip: number = 0;
  take: number = 10;
  totalCount: number = 0;
  showAll: boolean = false;

  insectaryDashboardService: InsectaryDashboardService = inject(InsectaryDashboardService);
  tableSearchRequest: ISearchRequest = {
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };

  siteName: string = '';
  itemDescName: string = 'ItemDescription';
  filtersActive: boolean = false;
  activeColumnFilters: Set<string> = new Set<string>();
  cols: any[] = [];
  approvalCount: string = '';

  customMatchModeOptions: SelectItem[] = [
    { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
    { label: 'Contains', value: FilterMatchMode.CONTAINS },
    { label: 'Not Contains', value: FilterMatchMode.NOT_CONTAINS },
    { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
    { label: 'Equals', value: FilterMatchMode.EQUALS },
    { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];
  dateMatchModeOptions: SelectItem[] = [
  { label: 'Date Is', value: FilterMatchMode.DATE_IS },
  { label: 'Date Is Not', value: FilterMatchMode.DATE_IS_NOT },
  { label: 'Date Before', value: FilterMatchMode.DATE_BEFORE },
  { label: 'Date After', value: FilterMatchMode.DATE_AFTER },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

numericMatchModeOptions: SelectItem[] = [
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Less Than or Equal', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Greater Than or Equal', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

  constructor(
    private msgService: MessageService,
    private appInit: AppInitService,
    private appInsights: AppInsightsService,
    private dictionaryCache: DictionaryCacheService,
    private router: Router,
    private filterService: FilterService
  ) {}

  ngOnInit(): void {
    this.pageVars.isReady = false;

    this.appUser = this.appInit.appUser;
    this.permissions = this.appInit.permissions;
    if (this.appUser) {
      if (!isStringEmpty(this.appUser.userSettingsJson) || this.appUser.userSettingsJson === '{}') {
        this.userSettingsJson = JSON.parse(this.appUser.userSettingsJson);
      } else {
        this.userSettingsJson = {};
      }

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
      this.siteName = this.userSettingsJson['Default Site'] || '';
    }

    if (this.permissions.insectaryApproveOrders) {
      this.loadApprovalCount();
    }

    const request = {
      undeliverableDates: this.dictionaryCache.get('UndeliverableDates'),
      undeliverableDays: this.dictionaryCache.get('UndeliverableDays')
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.pageVars.undeliveravbleDays = [...new Set(response.undeliverableDays.map((x) => x.text))];
        if (response.undeliverableDates.length > 0) {
          this.pageVars.undeliveravbleDates = response.undeliverableDates.map((x: any) => x.text).map((x: string) => this.formatDate(new Date(x)));
        }
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectaryDashboardComponent', action: 'InitLoad:Error' });
      });

    this.filterService.register('isEmpty', (value: any): boolean => {
      return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    });

    this.filterService.register('isNotEmpty', (value: any): boolean => {
      return value !== undefined && value !== null && (typeof value === 'string' && value.trim() !== '');
    });

    this.initializeColumns();
  }

  onCalendarReady() {
    this.pageVars.isReady = true;
  }

  async handleTableEvents(event: TableLazyLoadEvent) {
    this.activeColumnFilters.clear();
    this.tableSearchRequest.filters = [];

    if (!this.showAll) {
      this.tableSearchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'ItemStatus',
        logic: 'and',
        values: [
          <ISearchFilterValue>{ 
            operator: 'IN', 
            value: `${OrderItemStatus.Approved},${OrderItemStatus.InProgress}`
          },
        ]
      });
      this.tableSearchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'SiteName',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: `${this.siteName}` }]
      });
    }

    this.tableColumns.forEach((columnName) => {
      const columnFilters = event.filters![columnName] as FilterMetadata[];
      if (columnFilters && columnFilters.length > 0) {
        const tableFilter = <ISearchRequestFilter>{
          fieldname: columnName,
          logic: columnFilters[0].operator,
          values: []
        };

        columnFilters.forEach((filterValue) => {
          if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode.toUpperCase(),
              value: ''
            });
          } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode || `contains`,
              value: filterValue.matchMode?.startsWith(`date`) ? toShortDate(filterValue.value) : `${filterValue.value}`
            });
          }
        });

        if (tableFilter.values.length > 0) {
          this.tableSearchRequest.filters.push(tableFilter);
          this.activeColumnFilters.add(columnName);
        }
      }
    });

    this.tableSearchRequest.sorts = [];
    if (isNotEmpty(event.sortField)) {
      this.tableSearchRequest.sorts.push(<ISearchSort>{ name: event.sortField, direction: event.sortOrder === 1 ? 'ASC' : 'DESC' });
    } else {
      this.tableSearchRequest.sorts.push(<ISearchSort>{ name: 'ItemSequence', direction: 'ASC' });
    }

    this.tableSearchRequest.skip = event.first;
    this.tableSearchRequest.take = event.rows ?? 10;
    this.filtersActive = this.activeColumnFilters.size > 0 || isNotEmpty(event.sortField);

    await this.executeSearch();
  }

  getColumnHeaderStyle(columnName: string): any {
    return this.activeColumnFilters.has(columnName) ? { 'background-color': '#e0f7fa', 'border-bottom': '2px solid #006064' } : {};
  }

  initializeColumns() {
    const descField = this.itemDescName || 'ItemDescription';
    this.cols = [
      { field: 'OrderSequence', header: 'Order ID', type: 'text', template: 'link' },
      { field: 'RequesterName', header: 'Requester', type: 'text' },
      { field: 'ItemType', header: 'Item Type', type: 'text' },
      { field: descField, header: 'Item Description', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'FirstDelivery', header: 'First Delivery Date', type: 'date' },
      { field: 'LastDelivery', header: 'Last Delivery Date', type: 'date' },
      { field: 'InstanceCount', header: 'Occurrences', type: 'numeric' },
      { field: 'ItemStatus', header: 'Item Status', type: 'text' },
      { field: 'OrderName', header: 'Order Name', type: 'text' },
      { field: 'ItemSequence', header: 'Item ID', type: 'text' }
    ];
  }

  async toggleShowAll() {
    afterRender(() => {
      this.pageVars.isReady = false;
    });

    this.tableSearchRequest.filters = [];
    if (!this.showAll) {
      this.tableSearchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'ItemStatus',
        logic: 'and',
        values: [
          <ISearchFilterValue>{ 
            operator: 'IN', 
            value: `${OrderItemStatus.Approved},${OrderItemStatus.InProgress}` 
          },
        ]
      });
      this.tableSearchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'SiteName',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
      });
    }

    this.tableSearchRequest.skip = 0;
    this.tableSearchRequest.take = this.take ?? 10;

    await this.executeSearch();
  }

  async executeSearch() {
    const searchRequest: ISearchRequest = this.tableSearchRequest;
    searchRequest.columns = [...new Set([...this.tableColumns, getItemDescriptionColumn(this.userSettingsJson)])];

    firstValueFrom(this.insectaryDashboardService.getTable(searchRequest))
      .then(async (response) => {
        this.totalCount = response?.totalCount || 0;
        this.orders = JSON.parse(response?.results ?? `[]`);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectaryDashboardComponent', action: 'ExecuteSearch:Error' });
      })
      .finally(() => {
        setTimeout(() => {
          const calendarFilters = searchRequest.filters.filter(
            (f) => !f.values.some((v) => v.operator === 'isEmpty' || v.operator === 'isNotEmpty')
          );
          this.insectaryCalendar.loadCalendar([...calendarFilters]);
          this.pageVars.isReady = true;
        });
      });
  }

  approveOrder() {
    this.router.navigate(['/insectarydashboard/approve']);
  }

  insectPrep() {
    this.router.navigate(['/insectarydashboard/insectprep']);
  }

  substratePrep() {
    this.router.navigate(['/insectarydashboard/substrateprep']);
  }

  vendorOrders() {
    this.router.navigate(['/insectarydashboard/vendor']);
  }

  private formatDate(dt: Date): string {
    const year = dt.getFullYear().toString();
    const month = (dt.getMonth() + 1).toString().padStart(2, '0');
    const day = dt.getDate().toString().padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  private loadApprovalCount(): void {
    const approvalCountRequest = <ISearchRequest>{
      columns: [],
      filters: [],
      sorts: []
    };

    approvalCountRequest.filters.push(<ISearchRequestFilter>{
      fieldname: `SiteName`,
      logic: `and`,
      values: [<ISearchFilterValue>{ operator: `equals`, value: this.siteName }]
    });
    approvalCountRequest.filters.push(<ISearchRequestFilter>{
      fieldname: `OrderStatus`,
      logic: `and`,
      values: [
          <ISearchFilterValue>{ 
            operator: 'IN', 
            value: `${OrderStatus.Submitted},${OrderStatus.InProgress}` 
          },
        ]
      });

    firstValueFrom(this.insectaryDashboardService.getApprovalCount(approvalCountRequest))
      .then((response) => {
        this.approvalCount = `${response?.totalCount ?? 0}`;
      })
      .catch((err) => {
        this.approvalCount = `!`;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectaryDashboardComponent', action: 'LoadApprovalCount:Error' });
      });
  }

clearFilters(table: Table) {
  table.sortField = null;
  table.sortOrder = 1;
  table.clear(); 
  if (table.filters) {
    Object.keys(table.filters).forEach((key) => {
      const col = this.cols?.find((c: any) => c.field === key);
      const filters = table.filters[key] as any[];
      if (Array.isArray(filters)) {
        filters.forEach((f) => {
          if (f.matchMode === 'isEmpty' || f.matchMode === 'isNotEmpty') {
          if (col?.type === 'date') {
            f.matchMode = 'dateIs';
          } else if (col?.type === 'numeric') {
            f.matchMode = 'equals';
          } else {
            f.matchMode = 'contains';
          }
            f.value = null;
          }
        });
      }
    });
  }
  this.activeColumnFilters.clear();
  this.filtersActive = false;

  try {
    const stateKey = 'insectary-dashboard-table';
    const storedState = localStorage.getItem(stateKey);
    if (storedState) {
      const state = JSON.parse(storedState);
      delete state.sortField;
      delete state.sortOrder;
      delete state.filters;
      localStorage.setItem(stateKey, JSON.stringify(state));
    }
  } catch (e) {
    console.error('Error updating local storage in clearFilters', e);
  }

  setTimeout(() => {
    this.tableSearchRequest.filters = [];
    if (!this.showAll) {
      this.tableSearchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'ItemStatus',
        logic: 'and',
        values: [
          <ISearchFilterValue>{ 
            operator: 'IN', 
            value: `${OrderItemStatus.Approved},${OrderItemStatus.InProgress}`
          },
        ]
      });
      this.tableSearchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'SiteName',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: `${this.siteName}` }]
      });
    }
    this.tableSearchRequest.sorts = [{ name: 'ItemSequence', direction: 'ASC' }];
    this.tableSearchRequest.skip = 0;
    this.tableSearchRequest.take = this.take;
    this.executeSearch();
  }, 100);
}

  resetPage(table: Table) {
    table.clear();
    table.clearState();
    window.location.reload();
  }
}